import { Link, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { ArrowUpRight, FileText, MapPin, Search, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PLANS } from '@pageboss/shared';
import { PageContainer } from '@/components/page-container';
import { PageError } from '@/components/page-error';
import { PageHeader } from '@/components/page-header';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { locationRoutes } from '@/lib/route-map';

// =====================================================================
// Reports — "/app/b/:siteId/reports" (WP1 Task 10). A plain landing page
// that links out to reports that already live elsewhere in the panel — no
// new data source, no new report engine. Weekly digest is WP5's delivery
// mechanism (email); this screen says so honestly instead of faking a
// preview (spec §7 + CLAUDE.md honesty rules: no fake data). White-label
// tracking reports are Growth-only per CLAUDE.md's domain-rules table —
// gated on the effective tier, name read from PLANS labels (decision #20).
// =====================================================================

interface ReportLink {
  key: string;
  icon: LucideIcon;
  title: string;
  description: string;
  to: string;
}

export function Reports() {
  const meState = useMe();
  const { businessId = '', locationId: siteId } = useTenantParams();

  if (meState.kind === 'error') {
    // Keeps the page's own identity — same eyebrow and <h1> as the ready
    // state below — when /api/me fails. See components/page-error.tsx.
    return (
      <PageError
        eyebrow="Reports"
        title="All reports"
        message={meState.message}
        retry={invalidateMe}
      />
    );
  }
  if (meState.kind !== 'ready' || !siteId) return null;

  const tier = meState.me.usage.tier;
  const whiteLabelIncluded = tier !== 'free' && PLANS[tier].whiteLabel;

  const links: ReportLink[] = [
    {
      key: 'rank',
      icon: MapPin,
      title: 'Map rank checks',
      description: 'Where you show up on the map, search term by search term.',
      to: locationRoutes.googleProfileMap(businessId, siteId),
    },
    {
      key: 'ai',
      icon: Sparkles,
      title: 'AI visibility checks',
      description: 'Whether AI assistants mention you when someone asks.',
      to: locationRoutes.aiVisibility(businessId, siteId),
    },
    {
      key: 'aeo',
      icon: Search,
      title: 'AEO audits',
      description: 'What to fix so search engines and AI can read your site.',
      to: locationRoutes.aeo(businessId, siteId),
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Reports"
        title="All reports"
        description="Every report pageboss tracks for this site, in one place."
      />

      <ul className="flex flex-col gap-3">
        {links.map(({ key, icon: Icon, title, description, to }) => (
          <li key={key}>
            <Link
              to={to}
              className="flex items-center justify-between gap-4 rounded-xl border border-border-subtle bg-card p-5 transition-colors hover:bg-surface-sunken"
            >
              <div className="flex items-start gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary" aria-hidden="true" />
                <div>
                  <p className="font-medium text-foreground">{title}</p>
                  <p className="mt-0.5 text-sm text-text-secondary">{description}</p>
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>

      {/* Weekly digest — WP5 delivers the actual email; nothing to preview
          here yet, so the copy says exactly that instead of fabricating a
          sample. Dashed border marks it as "arriving", not broken. */}
      <section className="rounded-xl border border-dashed border-border-subtle bg-surface-sunken p-5">
        <div className="flex items-start gap-3">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary" aria-hidden="true" />
          <div>
            <p className="font-medium text-foreground">Weekly digest</p>
            <p className="mt-0.5 text-sm text-text-secondary">
              Coming with your weekly email — a summary of the week&rsquo;s checks, delivered to
              your inbox.
            </p>
          </div>
        </div>
      </section>

      <p className="text-xs text-text-tertiary">
        {whiteLabelIncluded
          ? `Your reports can be shared white-label, without pageboss branding — included on your ${PLANS.growth.label} plan.`
          : `White-label reports (no pageboss branding) are included on ${PLANS.growth.label}.`}
      </p>
    </PageContainer>
  );
}

export default Reports;
